// Refine chat (left pane) for the verbal-authoring review surface.
//
// Controlled component: the parent owns the chat turns + the
// "submitting" gate; this file just renders the conversation and emits
// `onSendTurn(text)` on submit. The parent posts to the refine endpoint
// and, on a successful response, appends an assistant turn + diff
// summary.
//
// Plan reference: `docs/planning/mfg_recipe_verbal_authoring.md` §3.4.

import { useCallback, useState } from "react";
import type { DictatedDraftChatTurn } from "@/lib/manufacturing/types/recipeDictation";
import { mfgMdInputClass, mfgMdPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingDictatedRecipeRefineChat({
  turns,
  submitting,
  onSendTurn,
}: {
  turns: ReadonlyArray<DictatedDraftChatTurn>;
  submitting: boolean;
  onSendTurn: (text: string) => void;
}) {
  const [draft, setDraft] = useState("");

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = draft.trim();
      if (!trimmed || submitting) return;
      onSendTurn(trimmed);
      setDraft("");
    },
    [draft, submitting, onSendTurn]
  );

  return (
    <div className="flex h-full min-h-[300px] flex-col">
      <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-zinc-300">
        Refine by chat
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2 dark:border-zinc-700 dark:bg-zinc-800/40">
        {turns.length === 0 ? (
          <div className="text-xs italic text-gray-500 dark:text-zinc-400">
            Type a refinement instruction (e.g. "rename to 8x10 Lofted" or "remove the
            EMT line"). The AI will produce a new draft and summarize what changed.
          </div>
        ) : (
          turns.map((turn) => <ChatTurn key={turn.id} turn={turn} />)
        )}
        {submitting ? (
          <div className="text-xs italic text-gray-500 dark:text-zinc-400">
            Applying refinement…
          </div>
        ) : null}
      </div>
      <form onSubmit={handleSubmit} className="mt-2 flex gap-2">
        <input
          type="text"
          className={mfgMdInputClass}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder='e.g. "rename to 8x10 Lofted" or "set door qty to 2"'
          disabled={submitting}
        />
        <button
          type="submit"
          className={mfgMdPrimaryBtnClass}
          disabled={submitting || draft.trim().length === 0}
        >
          {submitting ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}

function ChatTurn({ turn }: { turn: DictatedDraftChatTurn }) {
  if (turn.role === "user") {
    return (
      <div className="text-xs">
        <div className="font-semibold text-gray-700 dark:text-zinc-300">You</div>
        <div className="text-gray-900 dark:text-zinc-100">{turn.text}</div>
      </div>
    );
  }
  return (
    <div className="text-xs">
      <div className="font-semibold text-indigo-700 dark:text-indigo-300">AI</div>
      <div className="text-gray-900 dark:text-zinc-100">{turn.text}</div>
      {turn.diffSummary ? (
        <div className="mt-1 inline-block rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">
          {turn.diffSummary}
        </div>
      ) : null}
    </div>
  );
}
